import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

const initialState = {
  certificates: [] as Certificate[],
  isCertificatesRequested: false as boolean,
  companies: [] as Company[],
  isCompaniesRequested: false as boolean,
  certificateInstances: [] as CertificateInstance[],
  isCertificateInstancesRequested: false as boolean,
  users: [] as User[],
  isUsersRequested: false as boolean,
  certificateTypes: [] as CertificateType[],
  isCertificateTypesRequested: false as boolean,
  loggings: [] as Logging[],
  isLoggingsRequested: false as boolean,
  enrichedCertificates: [] as EnrichedCertificate[],
  announcements: [] as Announcement[],
  isAnnouncementsRequested: false as boolean,
}
const dataSlice = createSlice({
  name: 'interface',
  initialState,
  reducers: {
    setCertificates(state, action: PayloadAction<Certificate[]>) {
      state.certificates = action.payload
    },
    setIsCertificatesRequested(state, action: PayloadAction<boolean>) {
      state.isCertificatesRequested = action.payload
    },
    setCompanies(state, action: PayloadAction<Company[]>) {
      state.companies = action.payload
    },
    setIsCompaniesRequested(state, action: PayloadAction<boolean>) {
      state.isCompaniesRequested = action.payload
    },
    setCertificateInstances(
      state,
      action: PayloadAction<CertificateInstance[]>
    ) {
      state.certificateInstances = action.payload
    },
    setIsCertificateInstancesRequested(state, action: PayloadAction<boolean>) {
      state.isCertificateInstancesRequested = action.payload
    },
    setUsers(state, action: PayloadAction<User[]>) {
      state.users = action.payload
    },
    setIsUsersRequested(state, action: PayloadAction<boolean>) {
      state.isUsersRequested = action.payload
    },
    setCertificateTypes(state, action: PayloadAction<CertificateType[]>) {
      state.certificateTypes = action.payload
    },
    setIsCertificateTypesRequested(state, action: PayloadAction<boolean>) {
      state.isCertificateTypesRequested = action.payload
    },
    setLoggings(state, action: PayloadAction<Logging[]>) {
      state.loggings = action.payload
    },
    setIsLoggingsRequested(state, action: PayloadAction<boolean>) {
      state.isLoggingsRequested = action.payload
    },
    setEnrichedCertificates(
      state,
      action: PayloadAction<EnrichedCertificate[]>
    ) {
      state.enrichedCertificates = action.payload
    },
    setAnnouncements(state, action: PayloadAction<Announcement[]>) {
      state.announcements = action.payload
    },
    setIsAnnouncementsRequested(state, action: PayloadAction<boolean>) {
      state.isAnnouncementsRequested = action.payload
    },
  },
})

export const {
  setCertificates,
  setIsCertificatesRequested,
  setCompanies,
  setIsCompaniesRequested,
  setCertificateInstances,
  setIsCertificateInstancesRequested,
  setUsers,
  setIsUsersRequested,
  setCertificateTypes,
  setIsCertificateTypesRequested,
  setLoggings,
  setIsLoggingsRequested,
  setEnrichedCertificates,
  setAnnouncements,
  setIsAnnouncementsRequested,
} = dataSlice.actions

export default dataSlice.reducer

// 'Expired' is an extra calculated state based on status valid|prolonged + validFrom,ValidUntil&ExtendedUntil
export const deniedCertificateStatuses = ['Suspended', 'Withdrawn'] as const
export type DeniedCertificateStatus = (typeof deniedCertificateStatuses)[number]
export const certificateStatuses = [
  'Valid',
  'Prolonged',
  ...deniedCertificateStatuses,
] as const
export type CertificateStatus = (typeof certificateStatuses)[number]
export type CertificateStatusesExpired = 'Expired'
export const certificateStatusesExpired: CertificateStatusesExpired = 'Expired'
export type CertificateStatusesNotValidYet = 'NotValidYet'
export const certificateStatusesNotValidYet: CertificateStatusesNotValidYet =
  'NotValidYet'

export const CertificateScopeMaxLength = 5000
export interface certificateDeniedPeriod {
  deniedFrom: number
  deniedUntil: number
  deniedStatus: DeniedCertificateStatus
}
export interface Certificate {
  uuid: string // primary key
  certificateNumber: string
  scope: string
  naceCodes: string[]
  companyBranches: CertificateCompanyBranch[]
  status: CertificateStatus
  certificateInstanceVatNumber: string
  type: string // CertificateType.name
  validFrom: number
  validUntil: number
  extendedUntil: number | undefined
  issueDate: number
  isGroupCertificate: boolean
  deniedPeriods: undefined | certificateDeniedPeriod[]
  suspendedFrom: number | undefined
  withdrawnFrom: number | undefined
}
export interface CertificateCompanyBranch {
  allBranches?: boolean
  headquarters?: boolean
  vatNumber: string
  branchNumbers: string[]
}

export interface Company {
  vatNumber: string // primary key
  name: string
  legalForm: string
  email: string
  website: string
  addressLine1: string
  postalCode: string
  city: string
  countryCode: string
  naceCode: string
  branches: CompanyBranch[]
  accidentCounts: CompanyAccidentCount[]
  lastKpbCheck: number | undefined
  lastKpbUpdate: number | undefined
  kboInfo: string | undefined
  lastKpbCheckSuccess: boolean | undefined
}

export interface CompanyAccidentCount {
  year: number
  count: number
}

export interface CompanyBranch {
  number: string
  name: string | undefined
  addressLine1: string
  postalCode: string
  city: string
  countryCode: string
}

export interface CertificateInstance {
  vatNumber: string // primary key
  name: string
  email: string
  website: string
  telephone: string
  addressLine1: string
  postalCode: string
  city: string
  countryCode: string
  coordinators: CertificateInstanceCoordinator[]
}

export interface CertificateInstanceCoordinator {
  uuid: string
  name: string
  email: string
  telephone: string
}

export interface User {
  email: string // primary key, stored in lowercase
  isAdmin: boolean // if true, is admin, can manage all certificateInstances and some extra
  certificateInstanceVatNumber?: string // if not-empty, can manage that certificateInstance
}

export interface CertificateType {
  name: string
  isActive: boolean
  naceLimitation: string[]
  requiredCertificateNumbers: string[]
}

export interface Logging {
  uuid: string
  userEmail: string
  changeDate: number
  action: string
  details: string
}

export type CertificateCalculatedStatus =
  | CertificateStatus
  | CertificateStatusesExpired
  | CertificateStatusesNotValidYet
  | ''
export interface EnrichedCertificate extends Certificate {
  calculatedStatus: CertificateCalculatedStatus
  companyVatNumbers: string
  companyNames: string
  companyPostcodes: string
  certificateInstanceName: string
}

export type queryKboResponse =
  | {
      success: true
      lookupData: {
        vatNumber: string
        name: string
        legalForm: string
        addressLine1: string
        postalCode: string
        city: string
        countryCode: string
        naceCode: string
        branches: CompanyBranch[]
      }
    }
  | {
      success: false
      error?: string
    }

export const announcementMessageMaxSize = 300 * 1024 // ~300kb
export interface Announcement {
  uuid: string
  date: number
  title: string
  message: string
}
